import React from 'react'
import {
  Box,
  Button,
  ButtonBase,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import StarIcon from '@mui/icons-material/Star'
import StarBorderIcon from '@mui/icons-material/StarBorder'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import PreviewIcon from '@mui/icons-material/Preview'
import DragIndicatorIcon from '@mui/icons-material/DragIndicator'
import AccessTimeIcon from '@mui/icons-material/AccessTimeRounded'
import type { DraggableAttributes } from '@dnd-kit/core'
import type { SyntheticListenerMap } from '@dnd-kit/core/dist/hooks/utilities'
import { getCategoryColor, getChipColors } from '../../utils/colorByCategory'
import { shortenCategoryLabel } from '../../utils/categoryLabel'
import { formatRelativeTime } from '../../utils/relativeTime'
import useStationStore from '../../store/station'
import useShowClockPrepUsageStore from '../../store/showClockPrepUsageStore'
import { resolveEpochMs } from '../../utils/time'
import { shapeBorderRadius } from '../../theme/helpers'
import type { PrepListItem } from '../../api/prep'
import usePrepListStore from '../../store/prepListStore'
import useUserSettingsStore from '../../store/userSettings'
import { headlineClampSx } from './styles'
import EditPrepCategoriesModal from './EditPrepCategoriesModal'
import {
  capShowClockUsage,
  capShowClockUsageItems,
  formatShowClockUsageItemLabel,
  formatShowClockUsageLabel,
} from '../../utils/showClockUsageLabels'

export interface PrepCardProps {
  item: PrepListItem
  lineClamp: number
  isSuperadmin: boolean
  onOpenDetail: (id: number) => void
  onToggleMyPrep: (item: PrepListItem, next: boolean) => Promise<void>
  onDismiss: (item: PrepListItem) => Promise<void>
  onArchive?: (item: PrepListItem) => Promise<void>
  onAddToShowClock?: (params: { prepId: number; prepHeadline: string; launchedFromMyPrep: boolean }) => void
  launchedFromMyPrep?: boolean
  onHoverPrefetch?: (id: number) => void
  selected?: boolean
  onRefreshCategories?: (categoryIds: number[]) => void
  actionsSlot?: React.ReactNode
  dragHandle?: {
    attributes?: DraggableAttributes
    listeners?: SyntheticListenerMap | undefined
    setActivatorNodeRef?: (element: HTMLElement | null) => void
    tabIndex?: number
    disabled?: boolean
    onKeyDown?: React.KeyboardEventHandler<HTMLButtonElement>
    dragging?: boolean
  }
}

const clampTypographySx = (lines: number) => ({
  display: '-webkit-box',
  WebkitLineClamp: lines,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
})

const getShortStoryPreview = (item: PrepListItem): string => {
  if (item.short_story_preview) {
    return item.short_story_preview
  }
  if (item.short_story_preferred) {
    return item.short_story_preferred
  }
  if (item.short_story_neutral) {
    return item.short_story_neutral
  }
  return ''
}

const resolvePreviewText = (item: PrepListItem): string => {
  const preview = typeof item.preview === 'string' ? item.preview.trim() : ''
  if (preview.length > 0) {
    return preview
  }

  const tease = typeof item.tease === 'string' ? item.tease.trim() : ''
  return tease
}

const PrepCard: React.FC<PrepCardProps> = ({
  item,
  lineClamp,
  isSuperadmin,
  onOpenDetail,
  onToggleMyPrep,
  onDismiss,
  onArchive,
  onAddToShowClock,
  launchedFromMyPrep = false,
  onHoverPrefetch,
  onRefreshCategories,
  selected = false,
  actionsSlot,
  dragHandle,
}) => {
  const [action, setAction] = React.useState<'favorite' | 'dismiss' | 'archive' | null>(null)
  const [menuAnchor, setMenuAnchor] = React.useState<null | HTMLElement>(null)
  const [confirmOpen, setConfirmOpen] = React.useState(false)
  const [editCategoriesOpen, setEditCategoriesOpen] = React.useState(false)
  const headlineLines = useUserSettingsStore((state) => state.prep.headlineLines)
  const listShowImages = useUserSettingsStore((state) => state.prep.listShowImages)
  const listShowPreviewText = useUserSettingsStore((state) => state.prep.listShowPreviewText)
  const myIdsSet = usePrepListStore((state) => state.myIds)

  const categories = Array.isArray(item.categories) ? item.categories : []
  const cardCategories = categories.slice(0, 2)
  const primaryCategory = cardCategories[0] ?? null
  const colorSource = primaryCategory?.slug ?? 'prep'
  const colors = getCategoryColor(colorSource)
  const shortStoryPreview = React.useMemo(
    () => getShortStoryPreview(item),
    [item.short_story_neutral, item.short_story_preferred, item.short_story_preview],
  )
  const previewText = React.useMemo(() => {
    if (!listShowPreviewText) {
      return ''
    }
    return resolvePreviewText(item)
  }, [item.preview, item.tease, listShowPreviewText])
  const showPeekIcon = Boolean(previewText && item.short_story_present && shortStoryPreview)
  const isStarred = item.is_in_my_prep || myIdsSet.has(item.id)
  const imageThumb = typeof item.image?.thumb === 'string' ? item.image.thumb.trim() : ''
  const imageAlt = typeof item.image?.alt === 'string' ? item.image.alt : ''
  const showThumbnail = listShowImages && imageThumb.length > 0

  const stationId = useStationStore((state) => state.activeStation?.id ?? 0)
  const stationTimezone = useStationStore((state) => state.activeStation?.timezone)
  const publishedEpochMs = React.useMemo(
    () =>
      resolveEpochMs({
        epochSeconds: item.published_at_epoch ?? undefined,
        isoString: item.published_at_iso_utc ?? item.published_at ?? null,
      }),
    [item.published_at, item.published_at_epoch, item.published_at_iso_utc],
  )
  const relative = React.useMemo(() => {
    if (!publishedEpochMs) {
      return null
    }
    return formatRelativeTime(publishedEpochMs, stationTimezone)
  }, [publishedEpochMs, stationTimezone])
  const createdEpochMs = React.useMemo(
    () =>
      resolveEpochMs({
        epochSeconds: item.created_at_epoch ?? undefined,
        isoString: item.created_at_iso_utc ?? item.created_at ?? null,
      }),
    [item.created_at, item.created_at_epoch, item.created_at_iso_utc],
  )
  const addedRelative = React.useMemo(() => {
    if (!createdEpochMs) {
      return null
    }
    return formatRelativeTime(createdEpochMs, stationTimezone)
  }, [createdEpochMs, stationTimezone])

  const clocksByPrepId = useShowClockPrepUsageStore((state) => state.clocksByPrepId)
  const showClockUsage = React.useMemo(() => clocksByPrepId.get(item.id) ?? [], [clocksByPrepId, item.id])
  const inLiveOrFutureClock = showClockUsage.length > 0
  const tooltipTitle = React.useMemo(() => {
    if (!inLiveOrFutureClock) {
      return 'Add to Show Clock'
    }
    const { clocks, extraCount } = capShowClockUsage(showClockUsage, 3)
    return (
      <Box sx={{ py: 0.25 }}>
        <Typography variant="caption" sx={{ fontWeight: 700 }}>
          In live/future show clocks
        </Typography>
        <Box component="div" sx={{ mt: 0.5 }}>
          {clocks.map((clock, index) => {
            const usageItems = Array.isArray(clock.items) ? clock.items : []
            const { items, extraCount: extraItems } = capShowClockUsageItems(usageItems, 3)

            return (
              <Box key={clock.day_id} component="div" sx={{ mt: index === 0 ? 0 : 0.5 }}>
                <Box component="div" sx={{ typography: 'caption', fontWeight: 600 }}>
                  {formatShowClockUsageLabel(clock)}
                </Box>
                {items.map((item) => (
                  <Box key={item.day_item_id} component="div" sx={{ typography: 'caption', ml: 1 }}>
                    {formatShowClockUsageItemLabel(item)}
                  </Box>
                ))}
                {extraItems > 0 ? (
                  <Box component="div" sx={{ typography: 'caption', ml: 1, mt: 0.25 }}>
                    +{extraItems} more
                  </Box>
                ) : null}
              </Box>
            )
          })}
          {extraCount > 0 ? (
            <Box component="div" sx={{ typography: 'caption', mt: 0.25 }}>
              +{extraCount} more
            </Box>
          ) : null}
        </Box>
      </Box>
    )
  }, [inLiveOrFutureClock, showClockUsage])

  const handleToggleMyPrep = async () => {
    if (action) {
      return
    }
    setAction('favorite')
    try {
      await onToggleMyPrep(item, !isStarred)
    } finally {
      setAction(null)
    }
  }

  const handleDismiss = async () => {
    if (action) {
      return
    }
    setAction('dismiss')
    try {
      await onDismiss(item)
    } finally {
      setAction(null)
    }
  }

  const handleArchive = async () => {
    if (!onArchive) {
      return
    }
    if (action) {
      return
    }
    setAction('archive')
    try {
      await onArchive(item)
    } finally {
      setAction(null)
      setConfirmOpen(false)
      setMenuAnchor(null)
    }
  }

  const openMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setMenuAnchor(event.currentTarget)
  }

  const closeMenu = () => {
    setMenuAnchor(null)
  }

  React.useEffect(() => {
    if (!menuAnchor) {
      setConfirmOpen(false)
    }
  }, [menuAnchor])

  const handleOpenDetail = () => {
    onOpenDetail(item.id)
  }

  const handleHover = () => {
    if (onHoverPrefetch) {
      onHoverPrefetch(item.id)
    }
  }

  const handleAddToShowClock = () => {
    if (!onAddToShowClock || !stationId) {
      return
    }
    const headline = typeof item.headline === 'string' ? item.headline.trim() : ''
    onAddToShowClock({ prepId: item.id, prepHeadline: headline || 'Prep item', launchedFromMyPrep })
  }

  return (
    <Paper
      variant="outlined"
      sx={{
        position: 'relative',
        borderRadius: shapeBorderRadius,
        px: 2,
        py: 2,
        boxSizing: 'border-box',
        width: '100%',
        minWidth: 0,
        maxWidth: '100%',
        height: '100%',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        borderColor: selected ? 'primary.main' : undefined,
        boxShadow: selected ? 3 : dragHandle?.dragging ? 4 : 0,
        transition: 'box-shadow 120ms ease, border-color 120ms ease',
      }}
      onMouseEnter={handleHover}
      onFocus={handleHover}
      data-selected={selected ? 'true' : undefined}
    >
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          top: 0,
          bottom: 0,
          left: 0,
          pointerEvents: 'none',
          width: 3,
          borderTopLeftRadius: 'inherit',
          borderBottomLeftRadius: 'inherit',
          backgroundColor: colors.accent,
        }}
      />

      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
          minWidth: 0,
          maxWidth: '100%',
          gap: 1.5,
        }}
      >
        <Box
          sx={[
            {
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              minWidth: 0,
              position: 'relative',
            },
            showThumbnail
              ? {
                  pr: 6,
                  minHeight: 48,
                }
              : null,
          ]}
        >
          {showThumbnail ? (
            <Box
              component="img"
              src={imageThumb}
              alt={imageAlt}
              loading="lazy"
              onError={(event: React.SyntheticEvent<HTMLImageElement>) => {
                event.currentTarget.style.display = 'none'
              }}
              sx={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                objectFit: 'cover',
                position: 'absolute',
                top: 0,
                right: 0,
                boxShadow: (theme) => `0 0 0 1px ${theme.palette.divider}`,
                backgroundColor: 'background.paper',
                pointerEvents: 'none',
              }}
            />
          ) : null}
          <ButtonBase
            onClick={handleOpenDetail}
            sx={{
              textAlign: 'left',
              borderRadius: 2,
              px: 0,
              width: '100%',
              minWidth: 0,
              maxWidth: '100%',
              alignSelf: 'stretch',
              display: 'block',
              '&:focus-visible': {
                outline: 'none',
                boxShadow: (theme) => `0 0 0 2px ${theme.palette.primary.main}`,
              },
            }}
            aria-current={selected ? 'true' : undefined}
          >
            <Typography
              variant="h6"
              component="span"
              sx={[
                {
                  fontSize: '1rem',
                  fontWeight: 600,
                  lineHeight: 1.3,
                  maxWidth: '100%',
                },
                headlineClampSx(headlineLines),
              ]}
            >
              {item.headline}
            </Typography>
          </ButtonBase>

          <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                flex: 1,
                minWidth: 0,
                overflow: 'hidden',
              }}
            >
              {cardCategories.length > 0 ? (
                <Stack
                  direction="row"
                  spacing={0.75}
                  useFlexGap
                  flexWrap="nowrap"
                  sx={{ minWidth: 0, overflow: 'hidden' }}
                >
                  {cardCategories.map((category) => {
                    const chipColors = getChipColors(category.slug, { selected: true })
                    const { label, shortened } = shortenCategoryLabel(category.name)
                    const chipLabel = shortened ? label : category.name

                    return (
                      <Box key={category.slug} sx={{ minWidth: 0, flexShrink: 1 }}>
                        <Tooltip
                          title={shortened ? category.name : undefined}
                          disableHoverListener={!shortened}
                          disableFocusListener={!shortened}
                          disableTouchListener={!shortened}
                          enterDelay={300}
                          placement="top"
                        >
                          <Chip
                            label={chipLabel}
                            size="small"
                            aria-label={`Category ${category.name}`}
                            sx={{
                              borderRadius: 999,
                              fontWeight: 600,
                              height: 26,
                              borderWidth: 1,
                              borderStyle: 'solid',
                              backgroundColor: chipColors.background,
                              borderColor: chipColors.border,
                              color: chipColors.text,
                              maxWidth: 180,
                              flex: '0 0 auto',
                              '& .MuiChip-label': {
                                px: 1.5,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                minWidth: 0,
                                display: 'block',
                              },
                              '&:hover': {
                                backgroundColor: chipColors.hoverBackground,
                                borderColor: chipColors.hoverBorder,
                              },
                            }}
                          />
                        </Tooltip>
                      </Box>
                    )
                  })}
                </Stack>
              ) : null}
            </Box>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                gap: relative || addedRelative ? 0.75 : 0.5,
                flexShrink: 0,
                minWidth: 0,
              }}
            >
              {dragHandle ? (
                <Tooltip title="Reorder" enterDelay={300}>
                  <span>
                    <IconButton
                      aria-label="Reorder item"
                      size="small"
                      {...(dragHandle.attributes ?? {})}
                      {...(dragHandle.listeners ?? {})}
                      ref={dragHandle.setActivatorNodeRef as
                        | ((instance: HTMLButtonElement | null) => void)
                        | null
                        | undefined}
                      tabIndex={dragHandle.tabIndex ?? 0}
                      onKeyDown={dragHandle.onKeyDown}
                      disabled={dragHandle.disabled}
                      sx={{
                        p: 0.5,
                        mr: 0.5,
                        cursor: dragHandle.disabled ? 'not-allowed' : dragHandle.dragging ? 'grabbing' : 'grab',
                      }}
                    >
                      <DragIndicatorIcon fontSize="inherit" />
                    </IconButton>
                  </span>
                </Tooltip>
              ) : null}
              {relative || addedRelative ? (
                <Stack spacing={0} sx={{ flexShrink: 0, textAlign: 'right' }}>
                  {relative ? (
                    <Tooltip title={relative.tooltip ?? relative.label} enterDelay={300} placement="top">
                      <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.25 }}>
                        Published {relative.label}
                      </Typography>
                    </Tooltip>
                  ) : null}
                  {addedRelative ? (
                    <Tooltip title={addedRelative.tooltip ?? addedRelative.label} enterDelay={300} placement="top">
                      <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.25, opacity: 0.82 }}>
                        Added {addedRelative.label}
                      </Typography>
                    </Tooltip>
                  ) : null}
                </Stack>
              ) : null}

              <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                {actionsSlot ?? (
                  <>
                    <Tooltip title={isStarred ? 'Remove from My Prep' : 'Add to My Prep'} enterDelay={300}>
                      <span>
                        <IconButton
                          aria-label={isStarred ? 'Remove from My Prep' : 'Add to My Prep'}
                          onClick={handleToggleMyPrep}
                          disabled={action !== null}
                          size="small"
                          sx={{ p: 0.5, fontSize: 18 }}
                        >
                          {isStarred ? (
                            <StarIcon color="warning" fontSize="inherit" />
                          ) : (
                            <StarBorderIcon fontSize="inherit" />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>

                    <Tooltip title="Dismiss for me" enterDelay={300}>
                      <span>
                        <IconButton
                          aria-label="Dismiss for me"
                          onClick={handleDismiss}
                          disabled={action !== null}
                          size="small"
                          sx={{ p: 0.5, ml: 0.5, fontSize: 18 }}
                        >
                          <VisibilityOffIcon fontSize="inherit" />
                        </IconButton>
                      </span>
                    </Tooltip>

                    <Tooltip title={tooltipTitle} enterDelay={300}>
                      <span>
                        <IconButton
                          aria-label="Add to Show Clock"
                          onClick={handleAddToShowClock}
                          disabled={action !== null || !onAddToShowClock || !stationId}
                          size="small"
                          color={inLiveOrFutureClock ? 'success' : 'default'}
                          sx={{ p: 0.5, ml: 0.5, fontSize: 18 }}
                        >
                          <AccessTimeIcon fontSize="inherit" />
                        </IconButton>
                      </span>
                    </Tooltip>

                    {isSuperadmin && onArchive ? (
                      <>
                        <Tooltip title="More actions" enterDelay={300}>
                          <span>
                            <IconButton
                              aria-label="More actions"
                              onClick={openMenu}
                              disabled={action !== null}
                              size="small"
                              sx={{ p: 0.5, ml: 0.5, fontSize: 18 }}
                              aria-haspopup="menu"
                              aria-expanded={Boolean(menuAnchor)}
                            >
                              <MoreVertIcon fontSize="inherit" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Menu
                          anchorEl={menuAnchor}
                          open={Boolean(menuAnchor)}
                          onClose={closeMenu}
                          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                        >
                          <MenuItem
                            onClick={() => {
                              setMenuAnchor(null)
                              setConfirmOpen(true)
                            }}
                          >
                            Archive for everyone
                          </MenuItem>
                          <MenuItem
                            onClick={() => {
                              setMenuAnchor(null)
                              setEditCategoriesOpen(true)
                            }}
                          >
                            Edit category
                          </MenuItem>
                        </Menu>
                      </>
                    ) : null}
                  </>
                )}
              </Box>
            </Box>
          </Stack>
        </Box>

        {previewText ? (
          <Box
            sx={{
              display: 'flex',
              gap: 0.75,
              alignItems: 'flex-start',
              flex: 1,
              minHeight: 0,
              minWidth: 0,
            }}
          >
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ flex: 1, minHeight: 0, minWidth: 0, maxWidth: '100%', ...clampTypographySx(lineClamp) }}
            >
              {previewText}
            </Typography>
            {showPeekIcon ? (
              <Tooltip title="Peek full story" enterDelay={300}>
                <IconButton
                  aria-label="Peek full story"
                  size="small"
                  onClick={handleOpenDetail}
                  sx={{ flexShrink: 0, p: 0.5, ml: 0.5, fontSize: 18 }}
                >
                  <PreviewIcon fontSize="inherit" />
                </IconButton>
              </Tooltip>
            ) : null}
          </Box>
        ) : (
          <Box sx={{ flex: 1, minHeight: 0 }} />
        )}
      </Box>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} aria-labelledby={`archive-prep-${item.id}`}>
        <DialogTitle id={`archive-prep-${item.id}`}>Archive this prep item?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will remove the prep item for all presenters. You can undo this from the admin console if needed.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button color="error" onClick={handleArchive} disabled={action === 'archive'}>
            Archive for everyone
          </Button>
        </DialogActions>
      </Dialog>
      <EditPrepCategoriesModal
        open={editCategoriesOpen}
        item={item}
        onClose={() => setEditCategoriesOpen(false)}
        onSaved={(updated) => {
          const categoryIds =
            Array.isArray(updated.categories) && updated.categories.length > 0
              ? updated.categories
                  .map((entry) => (entry && typeof entry.id === 'number' ? Math.floor(entry.id) : null))
                  .filter((id): id is number => id !== null)
              : []
          const previousIds =
            Array.isArray(item.categories) && item.categories.length > 0
              ? item.categories
                  .map((entry) => (entry && typeof entry.id === 'number' ? Math.floor(entry.id) : null))
                  .filter((id): id is number => id !== null)
              : []
          const allIds = Array.from(new Set([...previousIds, ...categoryIds]))
          onRefreshCategories?.(categoryIds)
          if (allIds.length) {
            onRefreshCategories?.(allIds)
          }
        }}
      />
    </Paper>
  )
}

export default PrepCard
